import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { FormStepProps, ApplicationFormData } from '../../types/application';

const StepContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 10px;
  
  @media (min-width: 768px) {
    padding: 0;
  }
`;

const StepTitle = styled.h2`
  color: ${props => props.theme.primaryColor};
  font-size: 1.4rem;
  margin-bottom: 8px;
  font-weight: 600;
  
  @media (min-width: 768px) {
    font-size: 1.8rem;
  }
`;

const StepDescription = styled.p`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 20px;
  line-height: 1.6;
  
  @media (min-width: 768px) {
    font-size: 1rem;
    margin-bottom: 30px;
  }
`;

const FormGrid = styled.div`
  display: grid;
  gap: 15px;
  
  @media (min-width: 768px) {
    grid-template-columns: 2fr 1fr;
    gap: 30px;
  }
`;

const MainForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  
  @media (min-width: 768px) {
    gap: 20px;
  }
`;

const Sidebar = styled.div`
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  height: fit-content;
  
  @media (min-width: 768px) {
    padding: 20px;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #333;
  font-size: 14px;
  
  &.required::after {
    content: ' *';
    color: #d32f2f;
  }
`;

const Input = styled.input<{ hasError?: boolean }>`
  padding: 12px;
  border: 2px solid ${props => props.hasError ? '#d32f2f' : '#e0e0e0'};
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#d32f2f' : props.theme.primaryColor};
  }
  
  &::placeholder {
    color: #999;
  }
`;

const TextArea = styled.textarea<{ hasError?: boolean }>`
  padding: 12px;
  border: 2px solid ${props => props.hasError ? '#d32f2f' : '#e0e0e0'};
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#d32f2f' : props.theme.primaryColor};
  }
  
  &::placeholder {
    color: #999;
  }
`;

const ErrorMessage = styled.span`
  color: #d32f2f;
  font-size: 12px;
  margin-top: 4px;
`;

const SidebarTitle = styled.h3`
  color: ${props => props.theme.primaryColor};
  font-size: 1.1rem;
  margin-bottom: 15px;
  font-weight: 600;
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  
  li {
    color: #666;
    font-size: 14px;
    margin-bottom: 8px;
    padding-left: 16px;
    position: relative;
    
    &::before {
      content: '•';
      color: ${props => props.theme.primaryColor};
      position: absolute;
      left: 0;
    }
  }
`;

const ContactInformation: React.FC<FormStepProps & { onValidationChange: (isValid: boolean) => void }> = ({
  data,
  updateData,
  onValidationChange
}) => {
  const {
    register,
    watch,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      contactName: data.contactName,
      contactNumbers: data.contactNumbers,
      paymentMobile: data.paymentMobile,
      businessEmail: data.businessEmail,
      bankName: data.bankName,
      bankDetails: data.bankDetails
    }
  });

  const watchedValues = watch();

  useEffect(() => {
    updateData(watchedValues as Partial<ApplicationFormData>);
  }, [watchedValues, updateData]);

  useEffect(() => {
    // Validation - check required fields and email format
    const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    const hasRequiredFields = watchedValues.contactName && 
                             watchedValues.contactNumbers && 
                             watchedValues.businessEmail &&
                             watchedValues.bankName &&
                             watchedValues.bankDetails;
    const isEmailValid = watchedValues.businessEmail && emailPattern.test(watchedValues.businessEmail);
    
    onValidationChange(!!(hasRequiredFields && isEmailValid && Object.keys(errors).length === 0));
  }, [watchedValues, onValidationChange, errors]);

  return (
    <StepContainer>
      <StepTitle>Contact & Financial Information</StepTitle>
      <StepDescription>
        Provide contact details for payment operations and banking information for settlement of your payments.
      </StepDescription>

      <FormGrid>
        <MainForm>
          <FormGroup>
            <Label className="required">Contact name for payments</Label>
            <Input
              {...register('contactName')}
              placeholder="Usually the person in charge of Finance"
              hasError={!!errors.contactName}
            />
            {errors.contactName && <ErrorMessage>{errors.contactName.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label className="required">Contact Numbers</Label>
            <TextArea
              {...register('contactNumbers')}
              placeholder="You may provide more than one number with country codes e.g. +44 7xxxxxxxxx, +1 555xxxxxxx"
              hasError={!!errors.contactNumbers}
            />
            {errors.contactNumbers && <ErrorMessage>{errors.contactNumbers.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>Payment Notice Mobile Number</Label>
            <Input
              {...register('paymentMobile')}
              placeholder="If different from above (include country code)"
            />
          </FormGroup>

          <FormGroup>
            <Label className="required">Email</Label>
            <Input
              type="email"
              {...register('businessEmail', {
                required: 'Business email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Please enter a valid email address'
                }
              })}
              placeholder="Use business email that will be used for signing documents on behalf of the business"
              hasError={!!errors.businessEmail}
            />
            {errors.businessEmail && <ErrorMessage>{errors.businessEmail.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label className="required">Primary Bank Name</Label>
            <Input
              {...register('bankName')}
              placeholder="You should use a credible financial institution for the security of your funds"
              hasError={!!errors.bankName}
            />
            {errors.bankName && <ErrorMessage>{errors.bankName.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label className="required">Account Name and Banking Details</Label>
            <TextArea
              {...register('bankDetails')}
              placeholder="Your bank account should have the exact same name as your business name. Include IBAN/SWIFT for international accounts"
              hasError={!!errors.bankDetails}
            />
            {errors.bankDetails && <ErrorMessage>{errors.bankDetails.message}</ErrorMessage>}
          </FormGroup>
        </MainForm>

        <Sidebar>
          <SidebarTitle>Contact Requirements</SidebarTitle>
          <InfoList>
            <li>Business email address required</li>
            <li>International phone format (+country code)</li>
            <li>Finance contact for payment operations</li>
            <li>Used for statements and communications</li>
          </InfoList>

          <SidebarTitle style={{ marginTop: '25px' }}>Banking Requirements</SidebarTitle>
          <InfoList>
            <li>Major financial institution preferred</li>
            <li>Account name must match business name</li>
            <li>IBAN/SWIFT for international accounts</li>
            <li>Recent bank statements will be required</li>
          </InfoList>

          <SidebarTitle style={{ marginTop: '25px' }}>Settlement Details</SidebarTitle>
          <InfoList>
            <li>All Chinese payment settlements will go here</li>
            <li>Multicurrency support available</li>
            <li>Real-time settlement notifications</li>
            <li>Secure fund transfer protocols</li>
          </InfoList>
        </Sidebar>
      </FormGrid>
    </StepContainer>
  );
};

export default ContactInformation;